import {type FC, useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useEventGiftingClassifiedCategory} from '~/CreateFabricInvitation/routes/EventGifting/useEventGiftingClassifiedCategory';
import type {GiftOptionProps} from '~/CreateFabricInvitation/types/types';
import {FEATURE_QA_IDS} from '~/CreateFabricInvitation/utils/constants';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {
  selectCharityID,
  selectCrowdfundPurpose,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {EventFeature} from '~/common/types/event';
import {DonationIcon} from '~library/svg/DonationIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';
import {CrowdfundOverlay} from './CrowdfundOverlay';

export const CrowdfundOption: FC<GiftOptionProps> = ({
  hideIfInactive = false,
  hideIfActive = false,
  renderAs = 'block',
  controllerOpen = false,
  onToggleController,
  noOverlay = false,
}) => {
  const [showOverlay, setShowOverlay] = useState(controllerOpen);
  const crowdfundPurpose = useSelector(selectCrowdfundPurpose);
  const charityId = useSelector(selectCharityID);
  const {isWeddingEvent, isBabyEvent, isFuneralEvent} = useEventGiftingClassifiedCategory();

  useEffect(() => {
    setShowOverlay(controllerOpen);
  }, [controllerOpen]);

  const closeOverlay = useCallback(() => {
    if (onToggleController) onToggleController();
    else setShowOverlay(false);
  }, [onToggleController]);

  const onClickAddOrEdit = useCallback(() => {
    if (crowdfundPurpose || charityId) {
      scrollElementIntoViewById('module-donation', 'start', 0);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
    if (onToggleController) onToggleController();
    else setShowOverlay(true);
  }, [crowdfundPurpose, charityId, onToggleController]);

  let title = 'Crowdfunding';

  if (isFuneralEvent) {
    title = 'Raise funds for the impacted family ';
  }

  if (isWeddingEvent) {
    title = 'Support a cause you care about';
  }

  if (isBabyEvent) {
    title = 'Add a honeymoon or down payment fund';
  }

  const hide = Boolean((hideIfInactive && !crowdfundPurpose) || (hideIfActive && crowdfundPurpose));

  return (
    <InvitationWidget.Toggle
      editable
      renderAs={renderAs}
      title={renderAs === 'pill' ? 'Crowdfunding' : title}
      subtitle={crowdfundPurpose}
      filled={!!crowdfundPurpose}
      hide={hide}
      onClick={onClickAddOrEdit}
      startAdornment={<DonationIcon ratio={0.75} />}
      qaId={FEATURE_QA_IDS[EventFeature.CROWDFUNDING] as string}
      overlay={
        noOverlay ? null : <CrowdfundOverlay isOpen={showOverlay} closeOverlay={closeOverlay} />
      }
    />
  );
};
